/**
 * State management for the post-onboarding product tour.
 *
 * Tracks tour progress across 4 steps (Chat, Shortlist, Itinerary, Bookings)
 * and persists completion to the user profile attributes.
 */

import type { UserProfileContainer } from '#containers/UserProfileContainer.js';
import type { AppFlowsContainer } from '#flows.js';
import type { Trip } from '#models/TripModel.js';
import type { NavigationPort } from './navigation-port.js';
import type { SessionState } from './SessionState.js';
import type { TripPanelType, TripsState } from './TripsState.js';
import type { UserProfileState } from './UserProfileState.js';

import { createState } from '#lib/signals-utils.js';

export interface TourStep {
  panel: TripPanelType;
  headline: string;
  description: string;
  intro?: boolean;
  /** CSS selector for the element the tour dialog anchors to */
  anchorSelector?: string;
}

export const TOUR_STEPS: TourStep[] = [
  {
    panel: 'chat' as TripPanelType,
    intro: true,
    headline: 'Travel planning, reimagined',
    description:
      'Elara helps you discover destinations, find flights and hotels, and book your perfect trip. Let’s walk through a sample trip to show you how it all works.',
  },
  {
    panel: 'chat' as TripPanelType,
    headline: 'Chat with Elara',
    anchorSelector: '[data-tour-highlight="nav-chat"]',
    description: 'Plan a trip with Elara. Start with a dream or a departure date. Elara takes it from there.',
  },
  {
    panel: 'shortlist' as TripPanelType,
    anchorSelector: '[data-tour-highlight="nav-shortlist"]',
    headline: 'Shortlist',
    description: 'Save the options you love as you chat. Group them, compare them, and decide what makes the cut.',
  },
  {
    panel: 'itinerary' as TripPanelType,
    anchorSelector: '[data-tour-highlight="nav-itinerary"]',
    headline: 'Itinerary',
    description:
      'See your trip come together with a beautiful day-by-day walkthrough of your itinerary. With you on your trip, even when you’re not connected to the internet.',
  },
  {
    panel: 'bookings' as TripPanelType,
    anchorSelector: '[data-tour-highlight="nav-bookings"]',
    headline: 'Bookings',
    description:
      'All your flights, hotels, and activities in one place. When you’ve found the right hotel, one tap sends it to our team to book for you.',
  },
  {
    panel: 'bookings' as TripPanelType,
    anchorSelector: '[data-tour-highlight="nav-support"]',
    headline: 'Chat with Shannon, your dedicated, human, travel advisor',
    description: 'Whether you just want a quick gut check, or have a specific question, jump in here anytime.',
  },
  {
    panel: 'chat' as TripPanelType,
    headline: 'You’re all set!',
    description:
      'Start a new trip and tell Elara where you want to go, or keep exploring the demo to see what’s possible.',
  },
] as const;

export function createProductTourState(
  userProfileState: UserProfileState,
  tripsState: TripsState,
  routerState: NavigationPort,
  sessionState: SessionState,
  userProfile: UserProfileContainer,
  flows: AppFlowsContainer,
) {
  const state = createState({
    isActive: false,
    currentStep: 0,

    get currentStepConfig() {
      return TOUR_STEPS[this.currentStep];
    },

    get isLastStep(): boolean {
      return this.currentStep === TOUR_STEPS.length - 1;
    },

    /** CSS selector for the element the tour dialog anchors to */
    get anchorSelector(): string | undefined {
      return this.currentStepConfig?.anchorSelector;
    },

    get shouldShow(): boolean {
      const profile = userProfileState.profile;
      if (!profile) return false;
      if (!profile.onboardingCompletedAt) return false;
      if (profile.productTourCompletedAt) return false;
      if (!tripsState.current) return false;
      const route = routerState.currentRoute;
      if (!route?.path?.startsWith('/trip/')) return false;
      // Only auto-show on the demo trip
      const current = tripsState.current;
      if (!current?.isDemo) return false;
      if (!current?.demoDataReady) return false;
      return true;
    },

    /** Find the demo trip from the user's trip list */
    get demoTrip() {
      return tripsState.recentTrips.items.find((t: Trip) => t.isDemo) ?? undefined;
    },

    isCreatingDemoTrip: false,
    isResettingDemoTrip: false,
    isWaitingForDemoData: false,

    /** Whether the current trip is a demo trip that's still being seeded */
    get isDemoDataLoading(): boolean {
      const current = tripsState.current;
      if (!current?.isDemo) return false;
      return current.demoDataReady !== true;
    },

    async start() {
      this.currentStep = 0;

      // Find existing demo trip or create a new one
      const existingTripId = this.demoTrip?.id;

      if (existingTripId) {
        // Demo trip exists — navigate directly
        this.isActive = true;
        tripsState.loadTripDirectly(existingTripId);
        routerState.navigate(`/trip/${existingTripId}`);
        setTimeout(() => tripsState.switchToPanel('chat'), 300);
        return;
      }

      // No demo trip — create one. Server returns tripId quickly, seeds data in background.
      this.isCreatingDemoTrip = true;
      this.isWaitingForDemoData = true;
      try {
        const result = await flows.createDemoTrip({});
        const tripId = result.data.tripId;
        tripsState.loadTripDirectly(tripId);
        routerState.navigate(`/trip/${tripId}`);
      } catch (err) {
        console.error('[ProductTourState] Failed to create demo trip', { error: err });
        this.isWaitingForDemoData = false;
      } finally {
        this.isCreatingDemoTrip = false;
      }
    },

    get isFirstStep(): boolean {
      return this.currentStep === 0;
    },

    advance() {
      if (this.currentStep < TOUR_STEPS.length - 1) {
        this.currentStep = this.currentStep + 1;
        const nextPanel = TOUR_STEPS[this.currentStep]!.panel;
        tripsState.switchToPanel(nextPanel);
      } else {
        void this.complete();
      }
    },

    back() {
      if (this.currentStep > 0) {
        this.currentStep = this.currentStep - 1;
        const prevPanel = TOUR_STEPS[this.currentStep]!.panel;
        tripsState.switchToPanel(prevPanel);
      }
    },

    skip() {
      this.isActive = false;
      routerState.navigate('/');
      void this.persistCompletion();
    },

    /** End the tour and create a new trip for the user */
    planNewTrip() {
      this.isActive = false;
      void this.persistCompletion();
      const user = sessionState.user;
      if (user) {
        void tripsState.onNewTripAction({ currentUser: user });
      } else {
        routerState.navigate('/');
      }
    },

    /** End the tour and keep the user on the demo trip */
    keepExploring() {
      this.isActive = false;
      void this.persistCompletion();
    },

    async resetDemoTrip(tripId: string) {
      this.isResettingDemoTrip = true;
      try {
        await flows.resetDemoTrip({ tripId });
      } catch (err) {
        console.error('[ProductTourState] Failed to reset demo trip', { error: err });
      } finally {
        this.isResettingDemoTrip = false;
      }
    },

    async complete() {
      this.isActive = false;
      void this.persistCompletion();
    },

    async persistCompletion() {
      const { UserProfileClientModel } = userProfile;
      try {
        await UserProfileClientModel.update({ productTourCompletedAt: new Date().toISOString() });
      } catch (err) {
        console.error('[ProductTourState] Failed to persist tour completion', { error: err });
      }
    },
  });

  return state;
}

export type ProductTourState = ReturnType<typeof createProductTourState>;
